/**
 * 쓰기 도구 **승인 카드** (P1-#18 의 마지막 한 칸).
 *
 * ## ★★★ 왜 이 파일이 오늘에야 생겼나
 *
 * orchestrator 는 `confirm_required` 를 **토큰까지 실어 처음부터 보내고 있었고**, 재개 라우트
 * (`/orchestrate/continue`)도 이미 있었다. 없던 것은 **이 카드 하나**였다.
 *
 * 그래서 AI 가 **쓰기 도구를 하나도 가질 수 없었다** — 열어 봐야 사용자가 승인할 방법이 없으니.
 * 도메인 도구 가드가 「쓰기는 **확인 카드가 선 뒤에** 연다」고 적어 둔 것이 정확히 이 뜻이다.
 *
 * ## 무엇을 보여 주나
 *
 * 「무엇이 일어나는가」를 **서버가 적어 준 말 그대로**(`whatWillHappen`) 보여 준다. 화면이
 * 다시 쓰지 않는다 — 도구가 늘 때마다 여기를 고쳐야 한다면 결국 어긋난다.
 *
 * ⛔ **도구 이름·영향 범위는 안 보여 준다** (2026-09-21 사용자 지시). 종전엔 아래에
 *   「`mail.send_reply`  회사 밖으로 나갑니다」 한 줄이 있었는데, 도구 식별자 노출은
 *   원래 금지(`SCOPE_GUARD`)라 이 카드만 새고 있던 셈이다. 위험 경고는 머리글의
 *   `whatWillHappen`(「보낸 메일은 되돌릴 수 없습니다」)에 사람 말로 남는다.
 * ★ 기본 초점은 **취소**다 — 되돌릴 수 없는 일에서 엔터 한 번이 곧 실행이면 안 된다.
 */

import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, X } from '../../icons';
import { Button } from '../shared/Button';
import { fetchConfirmPreview, type AssistConfirm, type ConfirmPreviewLine } from '../../api/assist';

interface Props {
  confirm: AssistConfirm;
  onDecide: (decision: 'approve' | 'deny') => void;
}

export function AiConfirmCard({ confirm, onDecide }: Props): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [lines, setLines] = useState<ConfirmPreviewLine[] | null>(null);
  const denyRef = useRef<HTMLButtonElement>(null);

  /* ★★★ 「도구: mail.send_reply · 입력: {"mail_id":"1084"}」는 **사람이 읽을 말이 아니다.**
       받는 사람은 모델이 아니라 **서버가 원본 메일에서 푼다** — 그래서 물어본다.
       실패하면 `lines` 가 빈 배열이고 카드는 종전처럼 도구 이름만 보인다(덤이다). */
  useEffect(() => {
    let alive = true;
    void fetchConfirmPreview(confirm.tool, confirm.input).then((l) => { if (alive) setLines(l); });
    return () => { alive = false; };
  }, [confirm.tool, confirm.input]);

  /* ★ 되돌릴 수 없는 일이므로 초점은 «취소»에 둔다 — 엔터 한 번이 실행이면 안 된다. */
  useEffect(() => { denyRef.current?.focus(); }, []);

  const decide = (d: 'approve' | 'deny'): void => {
    if (busy) return;
    setBusy(true);
    onDecide(d);
  };

  return (
    <div className="aiconfirm" role="group" aria-label="실행 전 확인">
      <p className="aiconfirm__head">
        <AlertTriangle size={14} aria-hidden />
        실행하기 전에 확인해 주세요
      </p>
      {/* ★ 서버가 적어 준 말 그대로 — 화면이 다시 쓰지 않는다. */}
      <p className="aiconfirm__what">{confirm.whatWillHappen}</p>

      {/* 「받는 사람·제목·내용」 — 실행할 때와 **같은 함수**로 푼 값이다. */}
      {lines === null ? (
        <p className="aiconfirm__loading">무엇이 일어날지 확인하는 중…</p>
      ) : lines.length > 0 ? (
        <dl className="aiconfirm__lines">
          {lines.map((l) => (
            <div key={l.label} className="aiconfirm__line">
              <dt>{l.label}</dt>
              <dd>{l.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      <div className="aiconfirm__acts">
        <Button
          ref={denyRef}
          size="sm"
          variant="ghost"
          iconLeft={<X size={13} />}
          disabled={busy}
          onClick={() => decide('deny')}
        >
          하지 않기
        </Button>
        <Button
          size="sm"
          variant="primary"
          iconLeft={<Check size={13} />}
          disabled={busy}
          onClick={() => decide('approve')}
        >
          {busy ? '실행 중…' : '실행하기'}
        </Button>
      </div>
    </div>
  );
}
